<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import TopPageNavigation from '@/components/TopPageNavigation.vue';
import ConsultationNavigationTabs from '@/components/ConsultationNavigationTabs.vue';
import SectionCreateTab from '@/components/SectionTabs/SectionCreateTab.vue';
import SectionAnalyzeTab from '@/components/SectionTabs/SectionAnalyzeTab.vue';
import SectionEngageTab from '@/components/SectionTabs/SectionEngageTab.vue';

import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { getLanguageEnabled, getTranslation } from '@/composables/data-display-utils';
import { useConsultationsStore, useSectionsStore } from '@/stores';
import type { ConsultationDto, Locales, SectionDto } from '@/services/types/api.types';
import { useFormBreadcrumbOptions, useSectionBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { useRouteNumberParam } from '@/composables/use-route-param';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';

// PROPS
const props = withDefaults(
  defineProps<{
    isTemplate: boolean;
  }>(),
  {
    isTemplate: false,
  },
);

// CONST
const { t } = useI18n();

// STORE
const consultationsStore = useConsultationsStore();
const sectionsStore = useSectionsStore();

// REFS
const activeTab = ref<string>('create');
const sectionId = useRouteNumberParam('id');
const consultation = ref<ConsultationDto | undefined>(undefined);
const languageEnabled = ref<Locales[]>([]);
const createTabRef = ref<InstanceType<typeof SectionCreateTab> | null>(null);
const navigationTabsRef = ref<InstanceType<typeof ConsultationNavigationTabs> | null>(null);

const section = useWatchItemLoading<SectionDto>(sectionId, sectionsStore);

// COMPUTED
const isTemplate = toRef(props, 'isTemplate');

const { sectionOptions } = useSectionBreadcrumbOptions(
  computed(() => consultation.value?.id),
  isTemplate,
);

const { formOptions } = useFormBreadcrumbOptions(
  computed(() => section.value?.consultation_id),
  computed(() => section.value?.id),
  isTemplate,
);

const breadcrumbs = computed(() => {
  const breadcrumbItems: BreadcrumbElement[] = [
    {
      label: consultation.value?.title,
      to: {
        name: props.isTemplate ? 'consultation_template_details' : 'consultation_details',
        params: { id: consultation.value?.id },
      },
      icon: props.isTemplate ? 'o_settings' : 'o_folder_copy',
    },
    {
      label: section.value?.title,
      to: {
        name: props.isTemplate ? 'section_template_details' : 'section_details',
        params: { id: section.value?.id },
      },
      icon: 'o_folder',
      options: sectionOptions.value,
      activeId: section.value?.id,
    },
  ];
  if (section.value?.id) {
    breadcrumbItems.push({
      label: t('breadcrumbs.forms'),
      to: undefined,
      icon: 'o_article',
      options: formOptions.value,
    });
  }
  return breadcrumbItems;
});

// LIFECYCLE
watch(
  sectionId,
  async (newSectionId) => {
    consultation.value = undefined;
    languageEnabled.value = [];
    if (Number.isNaN(newSectionId) || newSectionId < 0) {
      return;
    }
    if (activeTab.value !== 'create') {
      activeTab.value = 'create';
      navigationTabsRef.value?.setActiveTab('create');
    }
    await sectionsStore.fetchSection(newSectionId);
  },
  { immediate: true },
);

watch(
  section,
  (newSection) => {
    if (!newSection?.consultation_id) {
      consultation.value = undefined;
      languageEnabled.value = [];
      return;
    }
    consultation.value = consultationsStore.getConsultation(newSection.consultation_id);
    languageEnabled.value = consultation.value ? getLanguageEnabled(consultation.value) : [];
  },
  { immediate: true },
);

// FUNCTIONS
const preview = () => {
  const previewUrl = sectionsStore.previewURL(sectionId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

const handleTabChange = (tabId: string) => {
  if (activeTab.value === 'create') {
    createTabRef.value?.executeNavigationGuard({
      onOk: () => (activeTab.value = tabId),
      onCancel: () => navigationTabsRef.value?.setActiveTab('create'),
    });
  } else {
    activeTab.value = tabId;
  }
};
</script>

<template>
  <q-page class="section-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      :to="
        consultation
          ? {
              name: props.isTemplate ? 'consultation_template_details' : 'consultation_details',
              params: { id: consultation.id },
            }
          : { name: 'home' }
      "
      @preview="preview"
    >
      <template #top-navigation>
        <consultation-navigation-tabs
          ref="navigationTabsRef"
          v-if="activeTab"
          :active-tab="activeTab"
          :is-template="isTemplate"
          :disabled-tabs="['analyze']"
          @update="handleTabChange"
        />
      </template>
    </top-page-navigation>
    <div class="title">
      <div>
        <q-icon name="o_folder" />
        <h1>{{ getTranslation(section, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="section">
      <div v-if="activeTab === 'create' && section?.id">
        <section-create-tab
          ref="createTabRef"
          :language-enabled="languageEnabled"
          :section-id="section.id"
          :is-template="isTemplate"
        />
      </div>
      <div v-else-if="activeTab === 'engage'">
        <section-engage-tab />
      </div>
      <div v-else-if="activeTab === 'analyze'">
        <section-analyze-tab />
      </div>
    </div>
  </q-page>
</template>
<style lang="scss">
.section-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    padding: 0 10px;

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }
}
</style>
